import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Phone, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Passwordless phone-OTP login/register (iter57).
 * Flow:
 *   Step 1 — user enters phone → POST /auth/phone/start
 *   Step 2 — user enters 6-digit code → POST /auth/phone/verify
 *              → cookie set server-side, refresh() re-hydrates AuthContext
 * Same endpoint transparently registers OR logs in — the shopper never sees the difference.
 */
export default function PhoneLogin() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const sendCode = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/start", { phone });
      if (data.dev_code) setDevCode(data.dev_code);
      setStep(2);
      toast.success(data.mock ? "Code sent (mock mode)" : "Code sent");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/verify", { phone, code, name: name || undefined });
      await refresh();
      toast.success(data.new_account ? "Welcome to Cyannect!" : "Welcome back!");
      const from = location.state?.from;
      navigate(from || "/dashboard");
    } catch (e) {
      setErr(e.response?.data?.detail || "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 py-16 relative">
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="blob blob-cyan blob-float w-[500px] h-[500px] top-10 -left-40" />
        <div className="blob blob-mint blob-float w-[500px] h-[500px] bottom-10 -right-40" style={{ animationDelay: "4s" }} />
      </div>

      <div className="max-w-md mx-auto relative z-10">
        <div className="glass-strong rounded-[28px] w-full p-8 fade-up" data-testid="phone-login-card">
          <img src="/cyannect-logo.png" alt="Cyannect" className="h-16 w-auto mx-auto mb-2" />
          <div className="pf-chip pf-chip-mint mx-auto w-fit flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Passwordless
          </div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-4 text-center text-slate-900">
            {step === 1 ? "Sign in with your phone" : "Enter your code"}
          </h1>
          <p className="text-sm text-slate-600 mt-2 text-center">
            {step === 1
              ? "We'll text you a one-time code. New here? We'll create your Cyannect account automatically."
              : `Sent to ${phone}. Codes expire in 10 minutes.`}
          </p>

          {step === 1 && (
            <form onSubmit={sendCode} className="mt-8 space-y-4" data-testid="phone-step-1">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Phone (E.164)
                </label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    type="tel"
                    required
                    placeholder="+14155552671"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.trim())}
                    className="pf-input w-full px-4 py-3 pl-11 text-sm"
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Include country code, e.g. <code>+1</code> for US/CA.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Name <span className="text-slate-400 font-normal normal-case">(optional — for new accounts)</span>
                </label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pf-input w-full px-4 py-3 text-sm"
                  data-testid="phone-name"
                />
              </div>
              {err && <div className="text-sm text-red-600 bg-red-50/70 rounded-xl px-4 py-2" data-testid="phone-error">{err}</div>}
              <button
                type="submit"
                disabled={loading || !phone}
                className="pf-btn-primary w-full py-3 rounded-full font-semibold disabled:opacity-60"
                data-testid="phone-send-btn"
              >
                {loading ? "Sending…" : "Send code"}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verify} className="mt-8 space-y-4" data-testid="phone-step-2">
              {devCode && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-800" data-testid="phone-dev-code">
                  Dev code (mock only): <span className="font-mono font-bold">{devCode}</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  6-digit code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoFocus
                  required
                  maxLength={8}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="pf-input w-full px-4 py-4 text-center text-2xl font-mono tracking-[0.4em]"
                  data-testid="phone-code-input"
                />
              </div>
              {err && <div className="text-sm text-red-600 bg-red-50/70 rounded-xl px-4 py-2" data-testid="phone-error">{err}</div>}
              <button
                type="submit"
                disabled={loading || code.length < 4}
                className="pf-btn-primary w-full py-3 rounded-full font-semibold disabled:opacity-60"
                data-testid="phone-verify-btn"
              >
                {loading ? "Verifying…" : "Verify & continue"}
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setCode(""); setDevCode(null); setErr(""); }}
                className="w-full text-sm text-slate-500 hover:text-cyan-700 flex items-center justify-center gap-1"
                data-testid="phone-back-btn"
              >
                <ArrowLeft className="w-3 h-3" /> Use a different number
              </button>
            </form>
          )}

          <div className="mt-6 text-sm text-slate-600 text-center">
            Prefer email? <Link to="/login" className="text-cyan-700 font-semibold hover:underline" data-testid="phone-email-link">
              Sign in with email
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
